// Syncs only the three clock-bound anchors (tutor/swim/co-op) to Google
// Calendar — never the daily six or weekly-board tiles, which stay inside
// the app's own views by design (her "any order, timer as cap not a clock
// commitment" framing applies to everything except these three). Pure reuse
// of Grove's existing Calendar write capability (lib/gcal.js) — no new
// OAuth scope needed, see the school-module-v2 plan's Google Calendar
// section. Follows ScheduleSitter.jsx's scheduleRecurring() pattern:
// one createEvent with an RRULE master, patchEvent on re-sync instead of
// creating a duplicate.
import { createEvent, patchEvent } from '../lib/gcal';
import { fetchSchoolCalendar, saveSchoolCalendar } from './api';
import { ANCHORS } from './seed/calendar';

const DAY_CODE = { 1: 'MO', 2: 'TU', 3: 'WE', 4: 'TH', 5: 'FR' };

function endTime(start, mins) {
  const [h, m] = start.split(':').map(Number);
  const total = h * 60 + m + mins;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

// Runs once per anchor per call — safe to call repeatedly (e.g. from a
// "Sync now" button): an anchor with a stored gcalEventId gets patched in
// place instead of creating a second event.
export async function syncAnchorsToCalendar(familyId, calendarId) {
  const calendar = await fetchSchoolCalendar(familyId);
  if (!calendar) throw new Error('No school calendar seeded yet');

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const untilStamp = `${calendar.endDate.replace(/-/g, '')}T235959Z`;
  const gcalEventIds = { ...(calendar.gcalEventIds || {}) };

  for (const [anchorId, cfg] of Object.entries(ANCHORS)) {
    const byDay = cfg.days.map(d => DAY_CODE[d]).join(',');
    const body = {
      summary: cfg.label,
      description: 'Synced from Grove\'s school module — edit the anchor in School → Setup, not here.',
      start: { dateTime: `${calendar.startDate}T${cfg.start}:00`, timeZone: tz },
      end: { dateTime: `${calendar.startDate}T${endTime(cfg.start, cfg.mins)}:00`, timeZone: tz },
      recurrence: [`RRULE:FREQ=WEEKLY;BYDAY=${byDay};UNTIL=${untilStamp}`],
    };

    const existingId = gcalEventIds[anchorId];
    if (existingId) {
      await patchEvent(calendarId, existingId, body);
    } else {
      const created = await createEvent(calendarId, body);
      gcalEventIds[anchorId] = created.id;
    }
  }

  await saveSchoolCalendar(familyId, { ...calendar, gcalCalendarId: calendarId, gcalEventIds });
  return gcalEventIds;
}
